import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel
} from './ui/dropdown-menu';
import { Settings, LogOut, Sparkles, Sun, Moon, Fingerprint, ChevronUp, Shield } from 'lucide-react';
import { Switch } from './ui/switch';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

export default function UserProfileDropdown({ user, collapsed }) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  if (!user) return null;

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <button
                className={`flex items-center gap-2.5 w-full rounded-md hover:bg-secondary/80 transition-colors ${collapsed ? 'p-2 justify-center' : 'px-3 py-2.5'}`}
                data-testid="user-profile-trigger"
              >
                <div className="w-7 h-7 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-600 text-[11px] font-semibold flex-shrink-0 ring-1 ring-amber-500/20">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    user.name?.[0]?.toUpperCase() || 'U'
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-[11px]">{user.name}</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center gap-2.5 w-full rounded-md hover:bg-secondary/80 transition-colors px-3 py-2.5"
            data-testid="user-profile-trigger"
          >
            <div className="w-7 h-7 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-600 text-[11px] font-semibold flex-shrink-0 ring-1 ring-amber-500/20">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                user.name?.[0]?.toUpperCase() || 'U'
              )}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[12px] font-medium text-foreground truncate leading-tight">{user.name}</p>
              <p className="text-[10px] text-muted-foreground truncate leading-tight">{user.ainaa_id || user.email}</p>
            </div>
            <ChevronUp className="w-3 h-3 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
          </button>
        </DropdownMenuTrigger>
      )}

      <DropdownMenuContent
        side={collapsed ? 'right' : 'top'}
        align="start"
        className="w-[220px] p-1"
        data-testid="user-profile-dropdown"
      >
        {/* AINAA ID Header */}
        <DropdownMenuLabel className="px-3 py-2">
          <p className="text-[12px] font-medium">{user.name}</p>
          <p className="text-[10px] font-mono text-muted-foreground">{user.ainaa_id || '—'}</p>
          <p className="text-[10px] text-muted-foreground">{user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* AINAA Details */}
        <DropdownMenuItem
          className="gap-2.5 text-[12px] py-1.5 cursor-pointer"
          onClick={() => navigate('/settings')}
          data-testid="dropdown-ainaa-details"
        >
          <Fingerprint className="w-3.5 h-3.5" strokeWidth={1.5} />
          AINAA ID Details
        </DropdownMenuItem>

        {/* Settings */}
        <DropdownMenuItem
          className="gap-2.5 text-[12px] py-1.5 cursor-pointer"
          onClick={() => navigate('/settings')}
          data-testid="dropdown-settings"
        >
          <Settings className="w-3.5 h-3.5" strokeWidth={1.5} />
          Settings
        </DropdownMenuItem>

        {/* Dark Mode Toggle */}
        <div
          className="flex items-center gap-2.5 px-2 py-1.5 rounded-sm text-[12px] text-foreground cursor-pointer hover:bg-secondary/80"
          onClick={(e) => { e.preventDefault(); toggleTheme(); }}
          data-testid="dropdown-dark-mode"
        >
          {theme === 'dark' ? <Sun className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Moon className="w-3.5 h-3.5" strokeWidth={1.5} />}
          <span className="flex-1">Dark mode</span>
          <Switch checked={theme === 'dark'} className="scale-75" />
        </div>

        {/* AINAA Insights */}
        <DropdownMenuItem
          className="gap-2.5 text-[12px] py-1.5 cursor-pointer text-amber-600 focus:text-amber-600"
          onClick={() => navigate('/ainaa')}
          data-testid="dropdown-ainaa-insights"
        >
          <Shield className="w-3.5 h-3.5" strokeWidth={1.5} />
          AINAA Insights
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Upgrade (greyed out) */}
        <DropdownMenuItem
          className="gap-2.5 text-[12px] py-1.5 text-muted-foreground/50 cursor-not-allowed"
          disabled
          data-testid="dropdown-upgrade"
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
          Upgrade to Pro
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Sign Out */}
        <DropdownMenuItem
          className="gap-2.5 text-[12px] py-1.5 cursor-pointer text-red-500 focus:text-red-500"
          onClick={logout}
          data-testid="dropdown-sign-out"
        >
          <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
